import { API_TRANSPORT, getProxySettings, pauseProxyExecution } from './proxySettings.js'

const errorMessages = {
  NOT_CONFIGURED: '转发服务尚未配置，请联系站点维护者',
  AUTH_REQUIRED: '请先设置访问口令（与游戏密码不同）',
  AUTH_INVALID: '访问口令不正确，请更新后验证',
  TARGET_DENIED: '此游戏服务器不在允许列表中',
  BAD_REQUEST: '转发请求格式不正确',
  TOO_LARGE: '转发请求超过大小限制',
  UPSTREAM_TIMEOUT: '游戏服务器响应超时',
  UPSTREAM_NETWORK: '无法连接游戏服务器',
  UPSTREAM_REDIRECT: '游戏服务器返回了不支持的跳转',
  PROXY_UNAVAILABLE: '转发服务暂时不可用或额度不足',
  PROXY_NETWORK: '连接转发服务失败',
  PROXY_PAUSED: '转发执行已暂停，请等待自动恢复或手动恢复',
}

export class ProxyTransportError extends Error {
  constructor(code) {
    super(errorMessages[code] || errorMessages.PROXY_UNAVAILABLE)
    this.name = 'ProxyTransportError'
    this.code = code
    this.retryable = false
  }
}

export class ProxyTransport {
  constructor({ mode = API_TRANSPORT, fetchImpl = (...args) => fetch(...args),
    settings = getProxySettings, pause = pauseProxyExecution, onError = null } = {}) {
    Object.assign(this, { mode, fetchImpl, settings, pause, onError })
  }

  fail(code) {
    const error = new ProxyTransportError(code)
    this.pause(error.message, code)
    this.onError?.(error)
    return error
  }

  async request({ endpoint, server, query, body, signal, directUrl, referrer }) {
    if (this.mode === 'direct') return this.fetchImpl(directUrl, {
      method: 'POST', headers: { accept: 'application/json', 'accept-language': 'zh-CN,zh;q=0.9,en;q=0.8',
        'content-type': 'application/x-www-form-urlencoded' },
      body, referrer, mode: 'cors', credentials: 'omit', signal,
    })
    if (this.settings().paused) throw new ProxyTransportError('PROXY_PAUSED')
    return this.proxyFetch(`/api/${endpoint}`, {
      method: 'POST', body: JSON.stringify({ server, query, body }), signal,
    }, 'upstream')
  }

  async verifyAccessKey({ signal, probe = false } = {}) {
    const response = await this.proxyFetch('/api/status', { method: 'GET', signal }, 'status', probe)
    try {
      const result = await response.json()
      if (result.ok !== true) throw new Error('Invalid status')
    } catch { throw probe ? new ProxyTransportError('PROXY_UNAVAILABLE') : this.fail('PROXY_UNAVAILABLE') }
    return true
  }

  // 暂停期间只允许固定的用户信息查询通过，不提供任意游戏请求的绕过开关。
  async probeMember(request) {
    const query = new URLSearchParams(request.query)
    if (query.get('c') !== 'member' || query.get('m') !== 'index' || request.body !== '') throw new Error('Invalid recovery probe')
    return this.proxyFetch('/api/game', { method: 'POST',
      body: JSON.stringify({ server: request.server, query: request.query, body: '' }), signal: request.signal }, 'upstream', true)
  }

  async proxyFetch(url, options, expectedMarker, probe = false) {
    const failure = code => probe ? new ProxyTransportError(code) : this.fail(code)
    const key = this.settings().accessKey
    if (!key) throw failure('AUTH_REQUIRED')
    let response
    try {
      const signal = AbortSignal.any([...(options.signal ? [options.signal] : []), AbortSignal.timeout(35000)])
      response = await this.fetchImpl(url, { ...options, signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json', 'X-Proxy-Key': key } })
    } catch (error) {
      if (options.signal?.aborted) throw error
      throw failure('PROXY_NETWORK')
    }
    if (options.signal?.aborted) throw new DOMException('Request cancelled', 'AbortError')
    const code = response.headers.get('X-Hanjiang-Proxy-Error')
    if (code) throw failure(Object.hasOwn(errorMessages, code) ? code : 'PROXY_UNAVAILABLE')
    if (response.headers.get('X-Hanjiang-Proxy-Result') !== expectedMarker) throw failure('PROXY_UNAVAILABLE')
    return response
  }
}
